import { Component, OnInit } from "@angular/core";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { AuthService } from "./login/auth.service";
import { filter, take } from "rxjs/operators";
import { IconSetService } from "@coreui/icons-angular";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../environments/environment";
import { iconSubset } from "./icons/icon-subset";
import { Title } from "@angular/platform-browser";
import { PushNotificationService } from "./services/push-notification.service";
import { OtaUpdateService, OtaUpdateInfo } from "./services/ota-update.service";
import { BackButtonService } from "./services/back-button.service";
import { NetworkErrorService } from "./services/network-error.service";
import { UserDataService } from "./user/user.service";
import { TokenStorageService } from "./login/token-storage.service";
import { isPublicRoute } from "./public-routes";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";

@Component({
  selector: "app-root",
  // OTA modal app.module.ts üzerinden AppComponent template'inde kullanılır
  template: `
    <router-outlet></router-outlet>
    <!-- OTA Zorunlu Güncelleme Modalı: güncelleme varsa görünür, kapatılamaz -->
    @if (pendingUpdate) {
    <app-ota-update-modal [updateInfo]="pendingUpdate"> </app-ota-update-modal>
    }
    <!-- Ağ Hatası Kutusu (Sadece Mobilde) -->
    @if (isNativePlatform && hasNetworkError) {
    <div class="mobile-network-error-toast">
      <div class="toast-content">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-wifi-off"
        >
          <line x1="1" y1="1" x2="23" y2="23"></line>
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
          <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
          <line x1="12" y1="20" x2="12.01" y2="20"></line>
        </svg>
        <span>İnternet bekleniyor, dilerseniz sayfayı yenileyebilirsiniz.</span>
      </div>
      <button class="retry-btn" (click)="reloadApp()">Yenile</button>
    </div>
    }
  `,
  standalone: false,
})
export class AppComponent implements OnInit {
  isNativePlatform = Capacitor.isNativePlatform();
  hasNetworkError = false;
  /** Güncel olmayan sürüm bilgisi — null ise modal görünmez */
  pendingUpdate: OtaUpdateInfo | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private titleService: Title,
    private iconSetService: IconSetService,
    private translate: TranslateService,
    private pushNotificationService: PushNotificationService,
    private otaUpdateService: OtaUpdateService,
    private backButtonService: BackButtonService,
    private networkErrorService: NetworkErrorService,
    private userDataService: UserDataService,
    private tokenStorage: TokenStorageService
  ) {
    this.titleService.setTitle("SAGEL");
    this.iconSetService.icons = { ...iconSubset };
    this.configureTranslations();
  }

  ngOnInit(): void {
    this.networkErrorService.hasError$.subscribe((hasError) => {
      this.hasNetworkError = hasError;
    });

    // Proaktif ağ dinlemeyi başlat (native'de anlık bağlantı takibi)
    this.networkErrorService.initialize();

    this.authService.restoreSessionFromStorage();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        take(1)
      )
      .subscribe(async (navEnd) => {
        // Mobil özel durum: Yönlendirme bittikten sonra arkadan token'ı kurtar
        await this.authService.rescueMobileSession();

        const url = navEnd.urlAfterRedirects;
        if (isPublicRoute(url)) {
          if (Capacitor.isNativePlatform()) {
            try {
              SplashScreen.hide({ fadeOutDuration: 300 });
            } catch (e) {}
          }
          return;
        }

        if (url === "/login") {
          if (this.authService.isAuthenticated()) {
            this.router.navigateByUrl(this.tokenStorage.landingUrl());
          } else {
            if (Capacitor.isNativePlatform()) {
              try {
                SplashScreen.hide({ fadeOutDuration: 300 });
              } catch (e) {}
            }
          }
          return;
        }

        // Stay on the refreshed route instead of bouncing to /dashboard.
        if (this.authService.isAuthenticated()) {
          // Populates global_user (notification badge, etc.) on every page, not just dashboard.
          // C29: a picker session has no tenant to load.
          if (!this.tokenStorage.isPickerSession()) {
            this.userDataService
              .refreshGlobalUser()
              .subscribe({ error: () => {} });
          }
        } else {
          this.router.navigateByUrl("/login");
        }
        if (Capacitor.isNativePlatform()) {
          try {
            SplashScreen.hide({ fadeOutDuration: 300 });
          } catch (e) {}
        }
      });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const title = this.getTitle(this.router.routerState.root);
        this.titleService.setTitle(title);
      });

    this.pushNotificationService.initialize();

    // Geri tuşu servisi başlat (sadece native/mobil platformda aktif)
    this.backButtonService.initialize();

    // OTA updater başlat (notifyAppReady + listener'lar)
    this.otaUpdateService.initialize();

    // Zorunlu güncelleme sinyalini dinle — modal aç
    this.otaUpdateService.updateRequired.subscribe(
      (updateInfo: OtaUpdateInfo) => {
        this.pendingUpdate = updateInfo;
      }
    );

    // Tablet / native: yön değişikliğinde Android WebView'ın viewport'u
    // yakınlaştırılmış tutma sorununu önle.
    if (Capacitor.isNativePlatform()) {
      // CSS tablet ayarlarını web'den izole etmek için body class'ı ekle.
      // Tüm tablet SCSS kuralları body.native-platform ile scope'lanmıştır.
      document.body.classList.add("native-platform");

      window.addEventListener("orientationchange", () => {
        setTimeout(() => {
          const viewport = document.querySelector(
            'meta[name="viewport"]'
          ) as HTMLMetaElement;
          if (viewport) {
            viewport.setAttribute(
              "content",
              "width=device-width, initial-scale=1, viewport-fit=cover"
            );
          }
        }, 300);
      });
    }
  }

  private getTitle(route: ActivatedRoute): string {
    const segment = route.snapshot.data["title"] || "";
    if (route.firstChild) {
      const child = this.getTitle(route.firstChild);
      return [segment, child].filter(Boolean).join(" | ");
    }
    return segment;
  }

  private configureTranslations(): void {
    const supportedLanguages = environment.supportedLanguages || [
      "en",
      "tr",
      "es",
      "el",
      "de",
    ];
    this.translate.addLangs(supportedLanguages);
    this.translate.setDefaultLang(environment.language);

    const browserLang =
      typeof navigator !== "undefined" && navigator.language
        ? navigator.language.split("-")[0]
        : environment.language;
    const initialLang = supportedLanguages.includes(browserLang)
      ? browserLang
      : environment.language;

    this.translate.use(initialLang);
  }

  reloadApp(): void {
    window.location.reload();
  }
}
